import { PartnerBucketGroupPrice, ValueType } from "./partnerDeal.interface";

// A merchant's online pricing comes in one of two shapes, and the business
// document says which one with `onlinePricingStructure`. The discriminator is
// the only safe way to tell them apart: migrated merchants keep group keys the
// other shape never prices, so the key set present is not evidence of anything.
export enum OnlinePricingStructure {
  FourBucket = "fourBucket",
  SixteenBucket = "sixteenBucket",
}

// Which card bucket the 16-bucket structure prices on its own instead of
// folding it into `european`. Mirrors the in-person PricingTemplate axis.
export enum OnlineCardSplitTemplate {
  Default = "default",
  EuropeanDebitSplit = "europeanDebitSplit",
  DomesticCreditSplit = "domesticCreditSplit",
}

// The bucket identifiers the backend puts in onlineMerchantServiceChargeFees'
// `name` for a 16-bucket merchant. They are field identifiers, not display
// strings — KBM owns the labels (see ONLINE_BUCKET_FIELD_LABELS).
export enum OnlineBucketField {
  DomesticDebit = "onlineDomesticDebit",
  DomesticCredit = "onlineDomesticCredit",
  EeaDebit = "onlineEeaDebit",
  EuropeanCredit = "onlineEuropeanCredit",
  DomesticCorporateDebit = "onlineDomesticCorporateDebit",
  DomesticCorporateCredit = "onlineDomesticCorporateCredit",
  EuropeanCorporateDebit = "onlineEuropeanCorporateDebit",
  EuropeanCorporateCredit = "onlineEuropeanCorporateCredit",
  InternationalDebit = "onlineInternationalDebit",
  InternationalCredit = "onlineInternationalCredit",
  InternationalCorporateDebit = "onlineInternationalCorporateDebit",
  InternationalCorporateCredit = "onlineInternationalCorporateCredit",
  Amex = "onlineAmex",
  AppleGooglePay = "onlineAppleGooglePay",
  IDeal = "onlineIDeal",
  Bancontact = "onlineBancontact",
}

// Groups only the four-bucket shape prices.
export type OnlineFourBucketGroupKey =
  "corporateCreditCards" | "nonCorporateCreditCards";

// Card groups only the 16-bucket shape prices. The wallet, AMEX and
// local-method groups are shared with the four-bucket shape.
export type OnlineSixteenBucketCardGroupKey =
  | "domesticDebitCards"
  | "domesticCreditCards"
  | "europeanDebitCards"
  | "european"
  | "internationalAndCommercial";

export type OnlineSharedGroupKey =
  "amex" | "appleGooglePay" | "iDeal" | "bancontact";

export type OnlineGroupKey =
  | OnlineFourBucketGroupKey
  | OnlineSixteenBucketCardGroupKey
  | OnlineSharedGroupKey;

// The merchant's stored group → price map, in whichever shape the
// discriminator names. Deliberately permissive: a 16-bucket merchant migrated
// on dev still carries inert four-bucket-era keys, and reading one must not be
// a type error — it is `onlinePricingStructure` that decides which keys mean
// anything, never the presence of a key.
export type BusinessOnlinePricing = Partial<
  Record<OnlineGroupKey, PartnerBucketGroupPrice>
>;

// Which buckets each priced group covers, as computed by the backend from the
// merchant's own template pair. Authoritative over any client-side table.
export type OnlineBucketGroupCoverageMap = Partial<
  Record<OnlineGroupKey, OnlineBucketField[]>
>;

// One bucket's resolved rate. `name` is a bucket field identifier and stays a
// plain string: the backend types this as its own merchant fee row, so it may
// name a bucket KBM has no label for yet.
export type OnlineBucketRateRow = {
  name: string;
  value: number;
  valueType: ValueType;
};

// The wire shape of the above: the preview endpoint sends one entry per priced
// group, in the order the backend prices them, not a keyed map. KBM normalizes
// it into OnlineBucketGroupCoverageMap before use. The backend names this entry
// type `OnlineBucketGroupCoverage`, so neither name here may be shortened to
// that — bare, it reads as the whole coverage on one side and one entry on the
// other, which is the misread these suffixes exist to prevent.
export type OnlineBucketGroupCoverageEntry = {
  group: OnlineGroupKey;
  buckets: OnlineBucketField[];
  // The rate each covered bucket actually carries, which is not derivable from
  // `buckets`: a bucket priced away from its group differs from its siblings.
  // Absent on payloads predating per-bucket rates, and absent for a merchant
  // KBM cannot fetch a preview for — see resolveOnlineBucketRates for what
  // stands in then.
  fees?: OnlineBucketRateRow[];
};
